import { zodResolver } from '@hookform/resolvers/zod';
import {
	type ShareShipmentEventDetails,
	ShareShipmentEventDetailsShareType,
	useRegisterShareShipmentEvent,
} from '@uturn/api/v1';
import { CopyToClipboard } from '@uturn/portal/components';
import {
	Button,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	sonner,
	Textarea,
} from '@uturn/ui-kit';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CharacterHint } from '../character-hint';
import {
	MAX_MESSAGE_CHARACTERS,
	type ShareLinkFormInputs,
	shareLinkFormSchema,
} from './share-shipment-schema';

export function ShareShipmentSheet({
	shipmentNumber = 0,
	openSheet,
	setOpenSheet,
}: {
	shipmentNumber: number;
	openSheet: boolean;
	setOpenSheet: (open: boolean) => void;
}) {
	const form = useForm<ShareLinkFormInputs>({
		resolver: zodResolver(shareLinkFormSchema),
		mode: 'onChange',
		defaultValues: {
			contactName: '',
			contactEmail: '',
			message: '',
		},
	});

	const { t } = useTranslation();
	const carrierPortalOrigin = import.meta.env.VITE_CARRIER_PORTAL_URL
		? new URL(import.meta.env.VITE_CARRIER_PORTAL_URL).origin
		: '';
	const shipmentUrl = `${carrierPortalOrigin}/dashboard/shipments/detail/${shipmentNumber}`;

	const shareShipment = useRegisterShareShipmentEvent({
		mutation: {
			onSuccess: () => {
				setOpenSheet(false);
				sonner.success(
					t(
						'pages.shipment-detail.modal.share-shipment.submit.success.description',
						'Shipment shared successfully',
					),
				);
				form.reset();
			},
			onError: () => {
				sonner.error(
					t(
						'pages.shipment-detail.modal.share-shipment.submit.error.description',
						'We were unable to share your shipment. Please try again later.',
					),
				);
			},
		},
	});

	const onSubmit = (data: ShareLinkFormInputs) => {
		const toSendData: ShareShipmentEventDetails = {
			contactName: data.contactName,
			contactEmail: data.contactEmail,
			message: data.message,
			shipmentNumber,
			shareType: ShareShipmentEventDetailsShareType.EMAIL,
		};

		shareShipment.mutate({ data: toSendData });
	};

	return (
		<Sheet
			open={openSheet}
			onOpenChange={(open) => {
				if (!open) {
					form.reset();
				}
				setOpenSheet(open);
			}}
		>
			<SheetContent>
				<SheetHeader>
					<SheetTitle>
						{t(
							'pages.shipment-detail.modal.share-shipment.heading',
							'Share shipment',
						)}
					</SheetTitle>
					<SheetDescription>
						{t(
							'pages.shipment-detail.modal.share-shipment.subheading',
							'Share this shipment with your carrier so they can view its details.',
						)}
					</SheetDescription>
				</SheetHeader>
				<div className="grid gap-5 pt-5">
					<div className="flex flex-col">
						<h3 className="text font-heading font-medium tracking-tight">
							{t(
								'pages.shipment-detail.modal.share-shipment.copy-url.heading',
								'Copy URL',
							)}
						</h3>
						<span className="text-sm">
							{shipmentUrl}
							<CopyToClipboard value={shipmentUrl} />
						</span>
					</div>
					<div className="flex flex-col gap-3">
						<h3 className="text font-heading font-medium tracking-tight">
							{t(
								'pages.shipment-detail.modal.share-shipment.send-link.heading',
								'Share with your carrier via email',
							)}
						</h3>
						<Form {...form}>
							<form
								onSubmit={form.handleSubmit(onSubmit)}
								className="col-span-3 grid grid-cols-1 space-y-4 sm:grid-cols-6"
							>
								<FormField
									control={form.control}
									name="contactName"
									render={({ field }) => (
										<FormItem className="sm:col-span-full">
											<FormLabel>
												{t(
													'pages.shipment-detail.modal.share-shipment.send-link.first-name',
													'Contact first name',
												)}
												<span className="ml-1 text-red-500">*</span>
											</FormLabel>
											<FormControl>
												<Input {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="contactEmail"
									render={({ field }) => (
										<FormItem className="sm:col-span-full">
											<FormLabel>
												{t(
													'pages.shipment-detail.modal.share-shipment.send-link.email',
													'Contact email',
												)}
												<span className="ml-1 text-red-500">*</span>
											</FormLabel>
											<FormControl>
												<Input {...field} type="email" />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="message"
									render={({ field }) => (
										<FormItem className="sm:col-span-full">
											<FormLabel>
												{t(
													'pages.shipment-detail.modal.share-shipment.send-link.message',
													'Your message',
												)}
											</FormLabel>
											<FormControl>
												<Textarea
													{...field}
													maxLength={MAX_MESSAGE_CHARACTERS}
												/>
											</FormControl>
											<CharacterHint
												currentCount={field.value?.length ?? 0}
												maxCharacters={MAX_MESSAGE_CHARACTERS}
											/>
											<FormMessage />
										</FormItem>
									)}
								/>

								<div className="flex justify-end sm:col-span-full">
									<Button
										type="submit"
										size="sm"
										disabled={
											!form.formState.isValid || shareShipment.isPending
										}
									>
										{t(
											'pages.shipment-detail.modal.share-shipment.send-link.submit',
											'Send email',
										)}
									</Button>
								</div>
							</form>
						</Form>
					</div>
				</div>
			</SheetContent>
		</Sheet>
	);
}
